import * as i18nCountries from 'i18n-iso-countries';
import { isNil } from 'lodash';
import isURL from 'validator/lib/isURL';
import {
  MappedTickerFormat,
  SecurityIdentifier,
  TickerFormat,
} from '../model/input/companies/security-identifier/types';
import { allCompanyTypes, CompanyType, isCompanyType } from '../model/input/companies/types';
import { isSupportedCurrency } from './currency';
const alpha3ByAlpha2 = i18nCountries.getAlpha2Codes();
const countryNameByAlpha2 = i18nCountries.getNames('en');
const alpha2Codes = Object.keys(alpha3ByAlpha2);
const alpha3Codes = Object.values(alpha3ByAlpha2);

export const alpha2ByAlpha3 = i18nCountries.getAlpha3Codes();

const alpha3ByCommonMispelling: { [key: string]: string } = {
  KOREA: 'KOR',
  'UNITED STATES': 'USA',
  RUSSIA: 'RUS',
  VIETNAM: 'VNM',
  SYRIA: 'SYR',
  MACEDONIA: 'MKD',
};

// Country names with , or () can not be matched back to codes.
const countries = [
  ...Object.keys(alpha3ByCommonMispelling),
  ...Object.values(countryNameByAlpha2)
    .map(c => (c.indexOf(',') > -1 ? c.substring(0, c.indexOf(',')) : c))
    .map(c => (c.indexOf(' (') > -1 ? c.substring(0, c.indexOf(' (')) : c)),
];
const countriesRegex = new RegExp(`\\b(${countries.join('|')})\\b`, 'gi');

export function extractCountry(name: string) {
  const countryMatches = name.match(countriesRegex);
  if (countryMatches && countryMatches.length === 1) {
    return parseCountry(countryMatches[0], false);
  }
  return undefined;
}

function initAlpha3ByCountryName() {
  const standard = Object.keys(countryNameByAlpha2).reduce(
    (reduced, alpha2Code) => {
      const name = countryNameByAlpha2[alpha2Code].toUpperCase().trim();
      reduced[name] = alpha3ByAlpha2[alpha2Code];
      return reduced;
    },
    {} as { [name: string]: string },
  );
  return { ...standard, ...alpha3ByCommonMispelling };
}
export const alpha3ByCountryName = initAlpha3ByCountryName();

export function parseCountry(value: string | undefined, errorIfNoMatch = true) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const country = value.toUpperCase().trim();
  const alpha3Code = alpha3Codes.includes(country)
    ? country
    : alpha2Codes.includes(country)
      ? alpha3ByAlpha2[country]
      : alpha3ByCountryName[country];
  if (!alpha3Code) {
    if (errorIfNoMatch) {
      throw Error(`Unable to parse country ${value}`);
    }
    return undefined;
  }
  return alpha3Code;
}

export function parseProductCodes(value: string) {
  if (!value) {
    return [];
  }
  const codeList = value
    .replace(/;/g, ',')
    .split(',')
    .map(code => code.trim())
    .filter(code => {
      if (code.length < 1) {
        return false;
      }
      return true;
    });
  return codeList;
}

export function parseSource(rowSource: string | undefined, altSource: string | undefined) {
  return (rowSource && rowSource.trim()) || (altSource && altSource.trim());
}

export function parseYear(rowYear: string | undefined, altYear: string | undefined) {
  const year = parsePositiveInteger(rowYear) || parsePositiveInteger(altYear);
  if (year && (year > new Date().getFullYear() || year < 1990)) {
    throw Error(`Susppicious year ${year}`);
  }
  return year;
}

export function parseMoney(value: string | undefined, currency: string | undefined) {
  const number = parseNumber(value);
  currency = currency ? currency.trim() : '';
  const multiplier = currency.length === 4 && currency.substring(0, 1).toLowerCase() === 'm' ? 1000_000 : 1;
  const currencyWithoutM = multiplier > 1 ? currency.substring(1) : currency;
  return [number && number * multiplier, parseCurrency(currencyWithoutM)] as const;
}

export function parseCurrency(value: string | undefined) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const trimmedUpper = value.trim().toUpperCase();
  if (!isSupportedCurrency(trimmedUpper)) {
    throw Error(`Unsuported currency ${value}`);
  }
  return trimmedUpper;
}

export function parseNumber(value: string | undefined | null) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const trimmed = value.trim();
  const numberRegex = /^((\+|-)?\d+)?\.?(\d+)?((e-|e\+|e)\d+(\.\d+)?)?$/i;
  const number = parseFloat(trimmed);
  if (!isFinite(number) || isNaN(number) || !numberRegex.test(trimmed)) {
    throw Error(`Unable to parse ${value} as number`);
  }
  return number;
}

export function parsePositiveNumber(value: string | undefined) {
  const number = parseNumber(value);
  if (number === undefined) {
    return undefined;
  }
  if (number < 0) {
    throw Error(`Unable to parse ${number} as positive`);
  }
  return number;
}

export function parsePositiveInteger(value: string | undefined) {
  const number = parsePositiveNumber(value);
  if (number === undefined) {
    return undefined;
  }
  if (!Number.isInteger(number)) {
    throw Error(`Unable to parse ${number} as integer`);
  }
  return number;
}

export function parseFilthyInputsAsPositiveInteger(value: string | undefined) {
  if (value === undefined) {
    return 0;
  }
  const firstDot = value.indexOf('.');
  const cleanValue = value.substr(0, firstDot !== -1 ? firstDot : value.length).replace(/\s/g, '');
  const number = parsePositiveInteger(cleanValue);
  if (number === undefined) {
    return 0;
  }
  return number;
}

export function isValidVATIN(value: string | undefined) {
  const vatinRegex = /^(?=([A-Z]{2,4}))\1(?![\W_]+$)(?=.{2,12}$)[0-9]*(?:[a-zA-Z][0-9]*){0,2}$/;
  return !!value && vatinRegex.test(value);
}

export function parseVATIN(value: string | undefined) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!isValidVATIN(trimmed)) {
    throw Error(`Unable to parse VATIN ${value}`);
  }
  return trimmed;
}

export function splitISINs(ISINs: string | undefined) {
  if (!ISINs) {
    return [];
  }
  return ISINs.split(/,|;|&/).map(isin => isin.trim());
}

export function areISINsValid(value: string | undefined) {
  if (!value) {
    return false;
  }
  const ISINs = splitISINs(value);
  return ISINs.every(validateISIN);
}

export function validateISIN(isin: string) {
  const ISINregex = /\b([A-Z]{2})((?![A-Z]{10}\b)[A-Z0-9]{10})\b/;
  return ISINregex.test(isin);
}

export function isValidTicker(value: string | undefined) {
  const tickerRegex = /^[0-9A-Z\s.]{1,15}:[A-Z]{1,10}$/;
  return !!value && tickerRegex.test(value);
}

export function parseTicker(value: string | undefined) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const upper = value.toUpperCase();
  if (!isValidTicker(upper)) {
    throw Error(`Unable to parse ticker ${value}`);
  }
  return upper;
}

export function tickerToString(ticker: TickerFormat) {
  const tickerName = ticker.tickerName;
  const exchange = ticker.exchange;
  return `${tickerName}:${exchange}`.toUpperCase();
}

export function tickerFromString(str: string): TickerFormat {
  const [tickerName, exchange] = str.split(':');
  return {
    tickerName,
    exchange,
  };
}

export function getTickerFromSecId(secId: SecurityIdentifier): MappedTickerFormat {
  const ticker = tickerFromString(secId.value);
  return {
    ...ticker,
    companyId: secId.companyId,
  };
}

/**
 * Canonical form of a case-insensitive identifier: trimmed and upper-cased, or `undefined` when
 * there is nothing left.
 *
 * ISIN, LEI, VATIN, CUSIP and ticker are upper-case by definition, but a spreadsheet cell can hold
 * any casing. Every validator here tests case-sensitively and every identifier lookup is a
 * case-sensitive SQL comparison, so a lower-case `nl0012969182` is rejected as malformed and then
 * fails to match — the value has to be canonicalised at intake, not merely validated more loosely.
 * (Loosening the ISIN regex to case-insensitive is specifically the wrong fix: it is unanchored with
 * a `(?![A-Z]{10}\b)` guard that stops ordinary words being read as ISINs, and that guard only works
 * on upper case.)
 *
 * Only for identifiers whose case carries no meaning. NOT for an Upright company id (slug-cased, and
 * looked up against the primary key), a customer external id (an opaque tenant string), or a website.
 */
export function normalizeIdentifierCase(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toUpperCase() : undefined;
}

export function parseISINs(value: string | undefined) {
  if (!value || !value.trim()) {
    return undefined;
  }
  if (!areISINsValid(value)) {
    throw Error(`Unable to parse ISINs ${value}`);
  }
  return splitISINs(value);
}

export function isValidLEI(value: string | undefined) {
  const LEIregex = /^[0-9A-Z]{20}$/;
  return !!value && LEIregex.test(value);
}

export function parseLEI(value: string | undefined) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!isValidLEI(trimmed)) {
    throw Error(`Unable to parse LEI ${value}`);
  }
  return trimmed;
}

export function isValidWikidataId(value: string | undefined) {
  const wikidataRegex = /^Q[0-9]+$/;
  return !!value && wikidataRegex.test(value);
}

export function parseWikidataId(value: string | undefined) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!isValidWikidataId(trimmed)) {
    throw Error(`Unable to parse wikidataId ${value}`);
  }
  return trimmed;
}

export function isValidURL(value: string | undefined) {
  return !!value && !!isURL(value, { require_protocol: true });
}

export function isValidInternalURL(value: string | undefined) {
  if (!value) return false;
  if (isURL(value, { require_protocol: false, require_host: false })) return true;
  if (isAnchorLink(value)) return true;
  if (isRelativePath(value)) return true;

  return false;
}

function isAnchorLink(value: string | undefined) {
  return !!value && value.startsWith('#');
}

function isRelativePath(value: string | undefined) {
  return !!value && value.startsWith('./');
}

export function parseURL(value: string | undefined, metadata?: { errorMsg?: string }) {
  if (!value || !value.trim()) {
    return undefined;
  }
  const cleanedValue = value.trim().replace(/\/$/, '');
  const parsedUrl = cleanedValue.indexOf('http') !== 0 ? `http://${cleanedValue}` : cleanedValue;
  if (isValidURL(parsedUrl)) {
    return parsedUrl;
  }
  throw Error(`Unable to parse URL ${value}. ${metadata?.errorMsg ?? ''}`);
}

export function stripWebsiteUrl(url: string) {
  return url.replace('https://', '').replace('http://', '').replace('www.', '').replace(/\/$/, '');
}

/**
 * Assert that values in "otherValues" are defined if "value" is NOT undefined
 */
export function assertAllIfOne(
  value: string | number | undefined,
  otherValues: Array<string | number | undefined>,
  recordName: string,
  hint: string = '',
) {
  const valueDefined = value !== undefined;
  const othersDefined = otherValues.map(v => v !== undefined);
  if (valueDefined && !othersDefined.every(truthy => truthy === valueDefined)) {
    throw Error(`${recordName} values missing or incorrect: ${value}, ${otherValues.join(', ')}. ${hint}`);
  }
}

export function assertValidCompanyType(
  companyType: string | undefined,
): asserts companyType is CompanyType | undefined {
  if (!isNil(companyType) && !isCompanyType(companyType))
    throw Error(`Type ${companyType} is invalid. Must be empty or one of ${allCompanyTypes.join(', ')}.`);
}
